<script setup lang="ts">
  import {storeToRefs} from 'pinia';
  import {computed, ref, watch} from 'vue';
  import {useUserStore} from '@core/store/UserStore';
  import snackbarService from '@core/services/SnackbarService';
  import {useSettingsStore} from '@core/store/SettingsStore';
  import ModulesService from '@core/services/ModulesService';
  import mgr from '@core/services/SecurityService';
  import {useI18n} from 'vue-i18n';
  import {RouterView, useRoute} from 'vue-router';
  import ErrorDialog from '@core/components/ErrorDialog.vue';
  import httpService from '@core/services/AxiosApiService';
  import ConfigProvider from '@core/services/ConfigProvider';
  import router, {routeGuard} from '@core/router/coreRouter';
  import LoadingIcon from '@core/components/LoadingIcon.vue';
  import Snackbar from '@core/components/Snackbar.vue';
  import Button from '@core/components/Button.vue';
  import {isRtlLocale} from '@core/utils/isRtlLocale';
  import {useEnvironmentStore} from '@internalDevportal/store/EnvironmentStore';

  const {locale} = useI18n({useScope: 'global'});
  const userStore = useUserStore();
  const settingsStore = useSettingsStore();
  const route = useRoute();

  const {language} = storeToRefs(settingsStore);
  const {t} = useI18n();

  const isInitialRouteReady = ref(false);
  const isInitialLoadingIconEnabled =
    ConfigProvider.value('initialLoadingIconEnabled') === 'true';

  // eslint-disable-next-line unicorn/prefer-top-level-await
  router.isReady().then(() => {
    isInitialRouteReady.value = true;
  });

  if (language.value !== locale.value) {
    locale.value = language.value;
  }
  //
  watch(
    () => locale.value,
    newLocale => {
      // Sync the i18n locale, page language (WCAG 3.1.1), and text direction
      document.documentElement.setAttribute('lang', newLocale);
      document.documentElement.setAttribute(
        'dir',
        isRtlLocale(newLocale) ? 'rtl' : 'ltr',
      );
    },
    {immediate: true},
  );

  watch(
    [() => settingsStore.darkModeEnabled, () => settingsStore.styles],
    ([isDark, styles]) => {
      const root = document.documentElement;
      const client = ConfigProvider.value('client');

      // Apply dark class to html element for Tailwind dark mode (needed for teleported content like modals)
      root.classList.toggle('dark', isDark);

      // Only apply styles from backend if we're not on an external client portal
      if (!client || client === 'default') {
        // Update Root CSS Variables based on current mode
        const colors = isDark ? styles.dark : styles.light;
        if (colors) {
          root.style.setProperty('--brand-primary', colors.primary);
          root.style.setProperty('--brand-secondary', colors.secondary);
          root.style.setProperty('--brand-accent', colors.accent);

          // Theme colors replacement for Vuetify;
          root.style.setProperty(
            '--background',
            isDark ? '#121212' : colors.background || '#FAFAFA',
          );
          root.style.setProperty(
            '--on-background',
            isDark ? '#f4f4f5' : '#18181b',
          );
        }

        const status = styles.status;
        if (status) {
          root.style.setProperty('--status-success', status.success);
          root.style.setProperty('--status-warning', status.warning);
          root.style.setProperty('--status-error', status.error);
          root.style.setProperty('--status-info', status.info);
        }
      }
    },
    {immediate: true, deep: true},
  );

  const darkModeEnabled = computed(() => settingsStore.darkModeEnabled);

  const user = computed(() => userStore.user);
  const login = userStore.login;
  const logout = userStore.logout;

  const EMPTY_REFERRER = settingsStore.EMPTY_REFERRER;
  const snackbars = computed(() => snackbarService.snackbars.value);
  const toggleSnackbarOff =
    snackbarService.toggleSnackbarOff.bind(snackbarService);

  EMPTY_REFERRER();

  // Initialize app state
  const initApp = async () => {
    try {
      if (user.value && user.value.access_token) {
        httpService.setAccessToken(user.value.access_token);
      }
      await ModulesService.getModules();
      httpService.setupInterceptors(t);
    } catch (error) {
      console.error('Failed to initialize app modules:', error);
      // Fallback: at least setup interceptors if possible
      httpService.setupInterceptors(t);
    }
  };

  initApp();

  mgr.events.addUserLoaded(async user => {
    await login(user);
    if (user.access_token) {
      httpService.setAccessToken(user.access_token);
    }
    routeGuard.invalidateEnvironmentAccess();
    if (route.name === 'noEnvironmentAccess') {
      const environmentStore = useEnvironmentStore();
      const result = await environmentStore.recheckEnvironmentAccess();
      if (result === 'allowed') {
        await router.replace({name: 'dashboard'});
      }
    }
  });
  mgr.events.addUserSignedOut(() => {
    logout();
  });

  function handleTokenExpired() {
    mgr
      .signinSilent()
      .then(user => {
        if (user?.access_token) {
          httpService.setAccessToken(user.access_token);
        }
        routeGuard.invalidateEnvironmentAccess();
      })
      .catch(error => {
        console.error('error while silent sign-in: ', error);
        logout();
        if (route.meta.public === true) {
          mgr.signoutRedirect();
        } else {
          mgr.signinRedirect();
        }
      });
  }

  mgr.events.addAccessTokenExpired(function () {
    handleTokenExpired();
  });
  mgr.events.addAccessTokenExpiring(function () {
    handleTokenExpired();
  });

  const errorDialogEnabled = ConfigProvider.value('errorDialogEnabled');
</script>

<template>
  <div
    id="inspire"
    :class="{dark: darkModeEnabled}"
    class="min-h-screen bg-(--background)">
    <div
      v-if="isInitialLoadingIconEnabled && !isInitialRouteReady"
      class="app-loading-overlay">
      <LoadingIcon
        class="app-loading-icon"
        :size="80" />
    </div>
    <RouterView />
    <div
      v-if="snackbars.length > 0"
      class="fixed bottom-0 right-0 z-2000 pointer-events-none">
      <Snackbar
        v-for="(snackbar, index) in snackbars"
        :key="snackbar.id"
        v-model="snackbar.enabled"
        :timeout="-1"
        :color="snackbar.color"
        :style="`bottom: ${index * 80 + 16}px; right: 16px; pointer-events: auto;`"
        class="z-2000">
        {{ snackbar.text }}
        <template #actions>
          <Button
            type="cancel"
            class="text-white border-white hover:bg-white/20"
            @click="toggleSnackbarOff(index)">
            {{ t('core.closeMsg') }}
          </Button>
        </template>
      </Snackbar>
    </div>
    <template v-if="errorDialogEnabled && errorDialogEnabled === 'true'">
      <ErrorDialog />
    </template>
  </div>
</template>

<style scoped>
  .app-loading-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.9);
    z-index: 9999;
  }

  :deep() .v-snack__action {
    width: 80px;
  }
  :deep() .v-data-table tbody tr:hover {
    background-color: v-bind(setDataTableHoverColor);
  }
  :deep() .v-data-table-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
</style>
